/**
 * NearbyCustomersModal — asks the browser for GPS, hits
 * /api/field-tools/customers/nearby, lists results, and on row click
 * navigates straight to the customer detail page with a hint to start
 * recording. Hides itself entirely when the user lacks permission.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, X, ArrowsClockwise, Warning, Microphone, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useFieldTools } from "@/lib/useFieldTools";

export default function NearbyCustomersModal({ open, onClose }) {
  const navigate = useNavigate();
  const { allowed, geocodingConfigured, radiusStopsM } = useFieldTools();
  const stops = (radiusStopsM && radiusStopsM.length ? radiusStopsM : [50, 100, 200, 500, 1000, 5000]);
  const defaultIdx = Math.max(0, Math.min(stops.length - 1, stops.indexOf(200) >= 0 ? stops.indexOf(200) : 2));
  const [radiusIdx, setRadiusIdx] = useState(defaultIdx);
  const [origin, setOrigin] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef(null);

  // Reset when reopened.
  useEffect(() => {
    if (!open) return;
    setItems([]);
    setError("");
    setOrigin(null);
    if (!geocodingConfigured) return;
    askLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function askLocation() {
    setError("");
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setError("Your browser doesn't support geolocation.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude,
                    accuracy_m: pos.coords.accuracy });
        loadNearby(pos.coords.latitude, pos.coords.longitude, stops[radiusIdx]);
      },
      (err) => {
        setLoading(false);
        setError(err?.message || "Could not read your location.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  }

  async function loadNearby(lat, lng, radius_m) {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/field-tools/customers/nearby", {
        params: { lat, lng, radius_m },
      });
      setItems(r.data.items || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 424) {
        setError("Geocoding is not configured yet. Ask a super-admin to add a Google Maps key.");
      } else {
        setError(e?.response?.data?.detail || "Failed to load nearby customers.");
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch when radius slider changes — debounced.
  function onRadiusChange(idx) {
    setRadiusIdx(idx);
    if (!origin) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadNearby(origin.lat, origin.lng, stops[idx]);
    }, 300);
  }

  function openCustomer(it) {
    // Pass a hint via sessionStorage so the next page knows to prompt
    // for a recording.
    try {
      sessionStorage.setItem("__field_tools_offer_record", JSON.stringify({
        customer_id: it.customer_id,
        customer_name: it.company_name || "",
        at: Date.now(),
      }));
    } catch { /* noop */ }
    onClose?.();
    navigate(`/compressors?customer=${encodeURIComponent(it.customer_id)}`);
  }

  if (!open) return null;
  if (!allowed) return null;

  return (
    <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-center bg-black/50 p-3 sm:p-6"
         data-testid="nearby-modal" onClick={() => onClose?.()}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-md shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <MapPin size={18} weight="fill" className="text-emerald-600" />
          <h2 className="text-sm font-bold tracking-tight flex-1">Find customers near me</h2>
          <button onClick={() => onClose?.()}
                  data-testid="nearby-modal-close"
                  className="text-slate-400 hover:text-slate-700">
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {!geocodingConfigured && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-[12px] text-amber-900"
                 data-testid="nearby-not-configured">
              <Warning size={14} weight="fill" className="inline mr-1" />
              Geocoding isn't configured yet. Ask your super-admin to add a Google Maps API key
              under <em>Admin → Field tools</em>.
            </div>
          )}

          {geocodingConfigured && origin && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
              <div className="flex items-center justify-between text-[11px] text-slate-600 mb-2">
                <span>📍 GPS accuracy ±{Math.round(origin.accuracy_m || 0)}m</span>
                <button onClick={askLocation}
                        data-testid="nearby-refresh-location"
                        className="text-blue-700 hover:underline font-bold uppercase tracking-wider text-[10px]">
                  Refresh
                </button>
              </div>
              <div>
                <label className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                  <span>Radius</span>
                  <span className="font-mono text-slate-900" data-testid="nearby-radius-label">
                    {stops[radiusIdx] >= 1000 ? `${stops[radiusIdx] / 1000} km` : `${stops[radiusIdx]} m`}
                  </span>
                </label>
                <input type="range" min={0} max={stops.length - 1} step={1}
                       value={radiusIdx}
                       onChange={(e) => onRadiusChange(parseInt(e.target.value, 10))}
                       data-testid="nearby-radius-slider"
                       className="w-full" />
                <div className="flex justify-between text-[9px] text-slate-400 mt-0.5">
                  {stops.map((s, i) => (
                    <span key={i} className={i === radiusIdx ? "text-slate-700 font-bold" : ""}>
                      {s >= 1000 ? `${s / 1000}k` : s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="text-[12px] text-rose-800 bg-rose-50 border border-rose-200 rounded-sm p-2">
              <Warning size={12} weight="fill" className="inline mr-1" /> {error}
            </div>
          )}

          {loading && (
            <div className="py-8 text-center" data-testid="nearby-loading">
              <ArrowsClockwise size={22} weight="bold" className="mx-auto animate-spin text-slate-400" />
              <p className="text-[11px] text-slate-500 mt-2">Looking up customers…</p>
            </div>
          )}

          {!loading && origin && items.length === 0 && !error && (
            <p className="text-[12px] text-slate-500 text-center py-6">
              No customers found within {stops[radiusIdx] >= 1000 ? `${stops[radiusIdx] / 1000} km` : `${stops[radiusIdx]} m`} of your location.
              <br />Try a wider radius — or check if their address has been geocoded yet.
            </p>
          )}

          {!loading && items.length > 0 && (
            <ul className="space-y-1.5" data-testid="nearby-list">
              {items.map((it) => (
                <li key={it.customer_id}>
                  <button onClick={() => openCustomer(it)}
                          data-testid={`nearby-customer-${it.customer_id}`}
                          className="w-full text-left flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-blue-300">
                    <Microphone size={14} weight="fill" className="text-rose-500 flex-shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-bold text-slate-900 truncate">
                        {it.company_name || it.customer_id}
                      </span>
                      <span className="block text-[11px] text-slate-500 truncate">{it.address}</span>
                    </span>
                    <span className="text-[10px] font-mono text-slate-500 flex-shrink-0">
                      {formatDist(it.distance_m)}
                    </span>
                    <CaretRight size={12} weight="bold" className="text-slate-400 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="text-[10px] text-slate-400 text-center">
            Only customers you have permission to view appear here.
          </p>
        </div>
      </div>
    </div>
  );
}

function formatDist(m) {
  if (m == null) return "—";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(2)} km`;
}
